/**
 * Where the unconnected entries go.
 *
 * Its own module because it is a different question from the force layout: the
 * simulation decides where related things sit, and this decides what to do with
 * everything the simulation has nothing to say about — in the live KB, 912 of
 * 3,427 entries.
 */

import { hashUnit, type LayoutNode } from './kb_graph_layout.js'
import { layoutBounds } from './kb_graph_bounds.js'

/**
 * Scatter unconnected entries in a ring around the connected component.
 *
 * Placed by sunflower (phyllotaxis) spiral rather than by angle × index: a
 * plain polar loop puts every node of an evenly divided ring on the same few
 * spokes, which draws lines that are not in the data. The golden angle never
 * repeats a spoke, so the ring fills evenly and reads as a scatter.
 *
 * Radius is chosen so the ring's AREA holds the nodes at a comfortable density,
 * which means it widens as the tail grows instead of overlapping into a smear.
 * Still fully deterministic: the jitter comes from the node id, not Math.random,
 * so the halo is in the same place on every visit.
 */
export function haloIsolated(
  nodes: readonly { id: string }[],
  placed: readonly LayoutNode[],
): LayoutNode[] {
  if (nodes.length === 0) return []
  const spacing = 15
  const b = layoutBounds(placed)
  const cx = placed.length > 0 ? (b.minX + b.maxX) / 2 : 0
  const cy = placed.length > 0 ? (b.minY + b.maxY) / 2 : 0

  // Start clear of the graph's own extent, never inside it.
  const graphRadius =
    placed.length > 0 ? Math.hypot(b.maxX - b.minX, b.maxY - b.minY) / 2 : spacing * 3
  const inner = graphRadius + spacing * 4
  // π(outer² − inner²) = n·spacing² — solve for the outer radius that gives
  // every node roughly `spacing` square units to itself.
  const outer = Math.sqrt(inner * inner + (nodes.length * spacing * spacing) / Math.PI)

  // 2.399963… rad — the golden angle, which is what makes the spiral not repeat.
  const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))

  return nodes.map((n, i) => {
    // sqrt keeps the density even: without it everything crowds the inner edge.
    const t = (i + 0.5) / nodes.length
    const r = Math.sqrt(inner * inner + t * (outer * outer - inner * inner))
    // A little id-seeded wobble so the spiral does not read as a machined part.
    const angle = i * GOLDEN_ANGLE + (hashUnit(n.id) - 0.5) * 0.35
    return {
      id: n.id,
      x: cx + Math.cos(angle) * r,
      y: cy + Math.sin(angle) * r,
      isolated: true,
    }
  })
}
